/**
 * Vendor-agnostic product analytics.
 *
 * Call sites must use this module — never import `posthog-js` or the Capacitor
 * plugin directly. The taxonomy doc at `docs/platform/product-analytics.md`
 * describes which events exist and what their properties mean.
 *
 * Typical usage:
 *
 *     import { analytics, AnalyticsEvent } from 'shared/analytics';
 *
 *     await analytics.initFromTenantSetting(apiKey, tenantOptIn);
 *     analytics.identify(user);
 *     analytics.group(tenantId);
 *     analytics.capture(AnalyticsEvent.AgentRunCompleted, { duration_ms });
 *
 * The wrapper is safe to call before init: every method no-ops until a driver
 * is wired and ready.
 */

import { AnalyticsEvent } from './events';
import { createPostHogDriver } from './posthog';
import type {
  AnalyticsDriver,
  AnalyticsUserProperties,
  EventProperties,
} from './types';

const DEFAULT_HOST = 'https://eu.i.posthog.com';

class Analytics {
  private driver: AnalyticsDriver = createPostHogDriver();
  private initPromise: Promise<void> | null = null;

  /**
   * Initialise PostHog if both an API key and the tenant opt-in are present.
   * Idempotent — repeated calls return the same promise.
   */
  async init(apiKey: string | undefined, tenantOptIn: boolean): Promise<void> {
    if (this.initPromise) return this.initPromise;
    this.initPromise = this.driver.init({
      apiKey,
      host: DEFAULT_HOST,
      tenantOptIn,
    });
    return this.initPromise;
  }

  identify(user: AnalyticsUserProperties): void {
    if (!this.driver.isReady()) return;
    const { user_id, ...props } = user;
    this.driver.identify(user_id, props);
  }

  group(tenantId: string, props: EventProperties = {}): void {
    this.driver.group(tenantId, props);
  }

  capture(event: string, props: EventProperties = {}): void {
    this.driver.capture(event, props);
  }

  pageview(path: string): void {
    this.driver.pageview(path);
  }

  /** Clear identity on logout so the next user starts a fresh distinct_id. */
  reset(): void {
    this.driver.reset();
    this.initPromise = null;
  }

  isReady(): boolean {
    return this.driver.isReady();
  }

  /** Test-only: swap the driver to a mock. */
  __setDriverForTesting(driver: AnalyticsDriver): void {
    this.driver = driver;
    this.initPromise = null;
  }
}

export const analytics = new Analytics();
export { AnalyticsEvent };
export { AnalyticsBootstrap } from './AnalyticsBootstrap';
export type {
  AnalyticsDriver,
  AnalyticsUserProperties,
  EventProperties,
} from './types';
